import type { AccountRepair, AccountRepairPreview } from "../../shared/account-repair";
import type { TrafficReport } from "../../shared/analytics";
import type {
  AccountSummary,
  AuditEntry,
  ClubRole,
  Invite,
  MembershipSpec,
  PublicGrant,
  StaffRole,
  SuperadminSummary,
} from "../../shared/auth";
import type { Award, AwardCandidate, PublicAward } from "../../shared/awards";
import type { BoardBundle } from "../../shared/board";
import type {
  ApplicationDecision,
  Club,
  ClubApplication,
  ClubMember,
  ClubPatch,
  ClubSummary,
  ClubTournament,
  ImportChoice,
  MemberPlayerLink,
} from "../../shared/club";
import type { DocContent, DocMeta } from "../../shared/docs";
import type { EntryDecision, EventEntry, PersonalEntry } from "../../shared/entries";
import { type ApiErrorCode, isApiErrorCode } from "../../shared/error-codes";
import type { ExportBundle } from "../../shared/export";
import type { FanChoice, FanState } from "../../shared/fan-voting";
import type { FunnelRow } from "../../shared/funnel";
import type { Person } from "../../shared/identity";
import type { ResolvedMapLink } from "../../shared/locations";
import type { SquadNow, TournamentNow } from "../../shared/now";
import type {
  IssuedPickupLink,
  PickupAvailabilityInput,
  PickupBoard,
  PickupLink,
  PickupLinkPeek,
  PlayerRequest,
  PlayerRequestInput,
} from "../../shared/pickup";
import type {
  AccountPatch,
  PersonalEvent,
  PlayerListing,
  PlayerProfile,
  RosterProfileLink,
} from "../../shared/player-profile";
import type { Retime } from "../../shared/retime";
import type { ScheduleBatchBody } from "../../shared/schedule-draft";
import type { Locale, SettingsPatch, TournamentSettings } from "../../shared/settings";
import type {
  PublicSpiritPayload,
  SpiritScores,
  SpiritSubmission,
  SpiritTeamView,
} from "../../shared/spirit";
import type { SpiritAward, SpiritAwardWrite } from "../../shared/spirit-awards";
import type { LiveReport, OperationsReport } from "../../shared/super-operations";
import type {
  SupportChat,
  SupportChatView,
  SupportMessage,
  SupportSide,
} from "../../shared/support";
import type { TeamRecord } from "../../shared/team-record";
import type { PublicTeamRoles, TeamLeadership, TeamRoleSelection } from "../../shared/team-roles";
import type {
  CloneInput,
  Tournament,
  TournamentInput,
  TournamentPatch,
  TournamentStats,
  TournamentStatus,
  TournamentSummary,
} from "../../shared/tournament";
import type {
  Division,
  Field,
  FieldId,
  GameAction,
  GameStats,
  ObsCommand,
  Player,
  Pool,
  ScheduledGame,
  StateMessage,
  Team,
} from "../../shared/types";
import { getLocale, translate } from "../i18n/runtime";

/**
 * Required on every mutation: see worker/router.ts. Defined here, in the
 * main-chunk module every surface already imports, so `lib/session.ts` (which
 * carries the capability tables) is never pulled in just to read a constant.
 */
export const CSRF_HEADER = "x-score-request";

/**
 * A refusal from the Worker, already in the reader's language.
 *
 * The wire carries a CODE and its parameters (`shared/error-codes.ts`): the
 * Worker has no business knowing what language the phone that called it is set
 * to. The sentence is built here, on the way in, which is why the thirteen
 * places that render `ApiError.message` did not have to change: they still show
 * the server's reason, it is simply now in Ukrainian when the reader is.
 *
 * An unrecognised code falls back to whatever string arrived, so a Worker
 * deployed ahead of (or behind) this client still says something.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** The wire code, for the rare caller that wants to branch on WHICH
     *  refusal rather than show it. */
    public readonly code?: ApiErrorCode,
  ) {
    super(message);
  }
}

/** The sentence for a refusal body, or null when it is not one we know. */
function sentenceFor(body: unknown): { text: string; code: ApiErrorCode } | null {
  const raw = (body as { error?: unknown } | null)?.error;
  if (!isApiErrorCode(raw)) return null;
  const params = (body as { params?: unknown }).params;
  const values =
    typeof params === "object" && params !== null
      ? (params as Record<string, string | number>)
      : undefined;
  return { text: translate(`error.${raw}`, values), code: raw };
}

/**
 * Every request carries the session cookie (same-origin, so it rides along on
 * its own) plus the CSRF header the Worker requires on mutations. There is no
 * key to attach any more: credentials live in an HttpOnly cookie the page
 * cannot read: see lib/session.ts.
 */
async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: {
      ...(body !== undefined ? { "content-type": "application/json" } : {}),
      // Harmless on GET, mandatory on everything else; sending it always means
      // a new call cannot forget it.
      [CSRF_HEADER]: "1",
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    // Surface the server's reason ("3 games still reference this team")
    // instead of a bare status code - dialogs show ApiError.message directly.
    const body = await res.json().catch(() => null);
    const known = sentenceFor(body);
    if (known !== null) throw new ApiError(res.status, known.text, known.code);
    const raw = (body as { error?: unknown } | null)?.error;
    throw new ApiError(
      res.status,
      typeof raw === "string" ? raw : `${method} ${path} -> ${res.status}`,
    );
  }
  return (await res.json()) as T;
}

export interface SpiritWrite {
  scores: SpiritScores;
  comment?: string;
  self?: SpiritScores;
}

/**
 * An organiser's write. Either a real score (with the two fields only they may
 * set), or a waiver, which carries no scores at all. A team's own payload
 * carrying `waived` or `commentHidden` is refused by the Worker, not ignored.
 */
export type SpiritAdminWrite =
  | (SpiritWrite & { commentHidden?: boolean })
  | { waived: true; commentHidden?: boolean };

/**
 * An award as the client states it. `announced` is a boolean intent, not a
 * timestamp: when it was announced is the server's fact to stamp, and a client
 * must not be able to backdate a ceremony.
 */
export interface AwardWrite {
  divisionId: string;
  title: string;
  candidates: AwardCandidate[];
  /** `null` clears the winner. `exactOptionalPropertyTypes` means "absent" and
   *  "explicitly none" cannot be the same value on the way out, and the Worker
   *  treats both as no decision. */
  decision?: { playerId: string; note?: string } | null;
  announced: boolean;
}

/** A merge as the client states it. The id and the timestamp are the
 *  server's. */
export interface PersonWrite {
  displayName: string;
  memberIds: string[];
}

export interface GrantRequest {
  role: StaffRole;
  label: string;
  fieldId?: string;
  teamId?: string;
  expiresAt: number;
}

/** An invitation as issued: the row, and the raw token exactly once. */
export interface IssuedInvite {
  invite: Invite;
  token: string;
  /** Whether the link also went out by mail (V3 Phase 0): an address was
   *  known. The dialog says so beside the link it still shows. */
  mailed: boolean;
}

/** What the client needs to offer the Telegram door (V3 Phase 1). */
export interface TelegramConfig {
  login: boolean;
  bot?: string;
  app?: string;
}

/** The super's Telegram card: what is configured, and where the webhook points. */
export interface SuperTelegram {
  /** Support chats (2026-09-16): how many wait, whether the owner's id is
   *  set, and whether an account carries it (the agent). */
  support: { open: number; configured: boolean; agent: boolean };
  bot: string | null;
  /** The command lists Telegram holds (default = Ukrainian, and English),
   *  and the menu button's type, read back live; null in log mode. */
  commands: { uk: string[] | null; en: string[] | null };
  menu: string | null;
  app: string | null;
  login: boolean;
  configured: boolean;
  secret: boolean;
  mode: "live" | "log";
  webhookUrl: string;
  webhook: { url: string; pending_update_count?: number; last_error_message?: string } | null;
  error?: string;
}

/** Whether notifications leave this deployment at all (V3 Phase 1): `log`
 *  means every message stays in the dev outbox. */
export interface OutboxSummary {
  mode: "live" | "log";
  mail: { mode: "resend" | "log"; from: string | null };
  telegram: { configured: boolean; login: boolean };
}

/**
 * THE PLATFORM: the registry and the superadmin surface. Nothing here belongs
 * to a tournament, so nothing here takes one (V2-ROADMAP.md Phase 2). The
 * session endpoints live in `lib/session.ts`.
 */
export const api = {
  // ---------- tournaments: the registry (V2 Phase 0) ----------
  /** Published and archived tournaments; a superadmin session also sees the
   *  drafts. The front page polls this. */
  tournaments: () => request<TournamentSummary[]>("GET", "/api/tournaments"),
  /** Card numbers per tournament id, for the front page. */
  tournamentStats: () => request<Record<string, TournamentStats>>("GET", "/api/tournaments/stats"),
  /** By id, or by slug: the same string for every v2 tournament, and the one
   *  lookup the legacy event (id `main`, slug chosen at adoption) needs. */
  tournament: (key: string) =>
    request<TournamentSummary>("GET", `/api/tournaments/${encodeURIComponent(key)}`),
  createTournament: (input: TournamentInput) =>
    request<Tournament>("POST", "/api/tournaments", input),
  updateTournament: (id: string, patch: TournamentPatch) =>
    request<Tournament>("PUT", `/api/tournaments/${encodeURIComponent(id)}`, patch),
  /** Any status move, with the superadmin's password (V2 Phase 4). */
  setTournamentStatus: (id: string, status: TournamentStatus, password: string) =>
    request<TournamentSummary>("POST", `/api/tournaments/${encodeURIComponent(id)}/status`, {
      status,
      password,
    }),
  /** A new draft from an old tournament's setup (V2 Phase 6): divisions,
   *  pools, fields, settings, teams (rosters optional); never the results. */
  cloneTournament: (id: string, input: CloneInput) =>
    request<{ tournament: Tournament; imported: Record<string, number> }>(
      "POST",
      `/api/tournaments/${encodeURIComponent(id)}/clone`,
      input,
    ),
  /** The existing event, registered under its permanent id `main` (D3).
   *  Refused after the first time. */
  adoptTournament: (input: TournamentInput) =>
    request<Tournament>("POST", "/api/tournaments/adopt", input),

  // ---------- superadmin surface (/super only) ----------
  superadmins: () => request<SuperadminSummary[]>("GET", "/api/super/admins"),
  /** `current` is the caller's own password: minting a superadmin re-prompts. */
  createSuperadmin: (a: { email: string; label: string; password: string; current: string }) =>
    request<SuperadminSummary>("POST", "/api/super/admins", a),
  disableSuperadmin: (id: string, password: string) =>
    request<{ ok: boolean }>("DELETE", `/api/super/admins/${id}`, { password }),
  allGrants: () => request<PublicGrant[]>("GET", "/api/super/grants"),

  // ---------- accounts and invitations (V2 Phase 3) ----------
  reactivateAccount: (id: string, password: string) =>
    request<{ ok: boolean }>("POST", `/api/super/accounts/${encodeURIComponent(id)}/reactivate`, {
      password,
    }),
  operations: () => request<OperationsReport>("GET", "/api/super/operations"),
  liveOperations: (tid: string) =>
    request<LiveReport>("GET", `/api/super/live/${encodeURIComponent(tid)}`),
  accounts: () => request<AccountSummary[]>("GET", "/api/super/accounts"),
  /** The player funnel, one row a month (2026-09-16). */
  superFunnel: () => request<FunnelRow[]>("GET", "/api/super/funnel"),
  /** Re-prompts for the superadmin's password, like disabling a superadmin. */
  disableAccount: (id: string, password: string) =>
    request<{ ok: boolean }>("DELETE", `/api/super/accounts/${id}`, { password }),
  revokeMembership: (id: string, membership: MembershipSpec, password: string) =>
    request<{ ok: boolean }>("DELETE", `/api/super/accounts/${id}/memberships`, {
      password,
      membership,
    }),
  /** A reset is a fresh invitation (D14): the link sets a new password, and
   *  since V3 Phase 0 also goes out by mail in this page's language. */
  previewAccountRepair: (plan: AccountRepair) =>
    request<{ ok: true; preview: AccountRepairPreview } | { ok: false; reason: string }>(
      "POST",
      "/api/super/accounts/repair-preview",
      plan,
    ),
  repairAccounts: (preview: AccountRepairPreview, password: string) =>
    request<{ ok: true; backupId: string } | { ok: false; reason: string }>(
      "POST",
      "/api/super/accounts/repair",
      { plan: preview.plan, fingerprint: preview.fingerprint, password },
    ),
  resetAccount: (id: string) =>
    request<IssuedInvite>("POST", `/api/super/accounts/${id}/reset`, { locale: getLocale() }),
  inviteOrganiser: (input: {
    tournamentId: string;
    label?: string;
    email?: string;
    accountId?: string;
  }) => request<IssuedInvite>("POST", "/api/super/invites", { ...input, locale: getLocale() }),
  /** The super's Telegram card, and the one act it offers. */
  superTelegram: () => request<SuperTelegram>("GET", "/api/super/telegram"),

  // ---------- support chats (2026-09-16, shared/support.ts) ----------
  support: {
    /** The signed-in account's open chat, or a new one (the web entry). */
    start: () => request<{ chat: SupportChat; created: boolean }>("POST", "/api/support/chats"),
    /** The bot's link token, traded for the chat cookie. */
    open: (id: string, token: string) =>
      request<{ chat: SupportChat; side: SupportSide }>(
        "POST",
        `/api/support/chats/${encodeURIComponent(id)}/open`,
        { token },
      ),
    list: () => request<SupportChat[]>("GET", "/api/support/chats"),
    chat: (id: string) =>
      request<SupportChatView>("GET", `/api/support/chats/${encodeURIComponent(id)}`),
    send: (id: string, text: string) =>
      request<{ message: SupportMessage; chat: SupportChat }>(
        "POST",
        `/api/support/chats/${encodeURIComponent(id)}/messages`,
        { text },
      ),
    sendPhoto: async (
      id: string,
      blob: Blob,
      width: number,
      height: number,
    ): Promise<{ message: SupportMessage; chat: SupportChat }> => {
      const path = `/api/support/chats/${encodeURIComponent(id)}/photo`;
      const res = await fetch(path, {
        method: "POST",
        headers: {
          "content-type": blob.type,
          "x-photo-width": String(width),
          "x-photo-height": String(height),
          [CSRF_HEADER]: "1",
        },
        body: blob,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const known = sentenceFor(body);
        throw new ApiError(res.status, known?.text ?? `POST ${path} -> ${res.status}`, known?.code);
      }
      return (await res.json()) as { message: SupportMessage; chat: SupportChat };
    },
    close: (id: string) =>
      request<{ chat: SupportChat }>("POST", `/api/support/chats/${encodeURIComponent(id)}/close`),
  },
  setTelegramWebhook: (password: string) =>
    request<{ ok: boolean; url: string }>("POST", "/api/super/telegram/webhook", { password }),
  /** Whether anything leaves this deployment: the People view says so. */
  outbox: () => request<OutboxSummary>("GET", "/api/super/outbox"),

  // ---------- the account's own record, and Telegram (V3 Phase 1) ----------
  telegramConfig: () => request<TelegramConfig>("GET", "/api/telegram/config"),
  account: () =>
    request<
      AccountSummary & {
        application: (ClubApplication & { clubName: string; clubSlug: string }) | null;
      }
    >("GET", "/api/account"),
  players: (q = "", club?: string) =>
    request<PlayerListing[]>(
      "GET",
      `/api/players?q=${encodeURIComponent(q)}${club ? `&club=${encodeURIComponent(club)}` : ""}`,
    ),
  playerDirectory: (q = "", club?: string, cursor?: string) =>
    request<import("../../shared/player-directory").PlayerDirectoryPage>(
      "GET",
      `/api/players?${new URLSearchParams({ page: "1", q, ...(club ? { club } : {}), ...(cursor ? { cursor } : {}) })}`,
    ),
  player: (id: string) => request<PlayerProfile>("GET", `/api/players/${encodeURIComponent(id)}`),
  /** The person's own photo (2026-09-11): the portrait the editor produced (WebP, or JPEG where the browser cannot encode WebP), raw in the body. */
  uploadPhoto: async (blob: Blob, focus: number): Promise<AccountSummary> => {
    const res = await fetch("/api/account/photo", {
      method: "POST",
      headers: { "content-type": blob.type, "x-photo-focus": focus.toFixed(2), [CSRF_HEADER]: "1" },
      body: blob,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const known = sentenceFor(body);
      throw new ApiError(
        res.status,
        known?.text ?? `POST /api/account/photo -> ${res.status}`,
        known?.code,
      );
    }
    return (await res.json()) as AccountSummary;
  },
  removePhoto: () => request<AccountSummary>("DELETE", "/api/account/photo"),
  dismissPhotoNotice: () => request<AccountSummary>("DELETE", "/api/account/photo/notice"),
  /** A captain of one of the person's clubs, or the console, takes a photo down and says why. */
  moderatePhoto: (clubId: string, accountId: string, reason: string) =>
    request<{ ok: boolean }>(
      "DELETE",
      `/api/clubs/${encodeURIComponent(clubId)}/members/${accountId}/photo`,
      { reason },
    ),
  personalEntries: () => request<PersonalEntry[]>("GET", "/api/account/entries"),
  playerRequests: () => request<PlayerRequest[]>("GET", "/api/account/player-requests"),
  requestPlayer: (body: PlayerRequestInput) =>
    request<PlayerRequest>("POST", "/api/account/player-requests", body),
  decidePlayer: (id: string, body: EntryDecision) =>
    request<PlayerRequest>(
      "POST",
      `/api/account/player-requests/${encodeURIComponent(id)}/decision`,
      body,
    ),
  withdrawPlayer: (id: string) =>
    request<{ ok: boolean }>("DELETE", `/api/account/player-requests/${encodeURIComponent(id)}`),
  /** What a squad's pickup invite link names. The token rides a header, never the URL. */
  peekPickupLink: async (token: string): Promise<PickupLinkPeek> => {
    const res = await fetch("/api/pickup-links/peek", {
      headers: { [CSRF_HEADER]: "1", "x-score-invite": token },
    });
    const body = await res.json().catch(() => null);
    if (res.ok) return body as PickupLinkPeek;
    const known = sentenceFor(body);
    throw new ApiError(res.status, known?.text ?? `peek -> ${res.status}`, known?.code);
  },
  /** The player's tap on that link: signed in, with their own number. */
  acceptPickupLink: (token: string, number: string) =>
    request<{ request: PlayerRequest; slug: string }>("POST", "/api/pickup-links/accept", {
      token,
      number,
    }),
  personalEvents: () => request<PersonalEvent[]>("GET", "/api/account/events"),
  /** The person's squads at current events, each with its live game, fresh result or next kickoff (2026-09-16). */
  accountNext: () => request<SquadNow[]>("GET", "/api/account/next"),
  updateAccount: (patch: AccountPatch) => request<AccountSummary>("PUT", "/api/account", patch),
  deleteAccount: (body: { password?: string; confirm?: boolean }) =>
    request<{ ok: boolean }>("DELETE", "/api/account", body),
  /** Connect from inside Telegram, in one tap: the launch this page arrived with. */
  linkTelegramLaunch: (initData: string) =>
    request<AccountSummary>("POST", "/api/account/telegram", { initData }),
  unlinkTelegram: () => request<{ ok: boolean }>("DELETE", "/api/account/telegram"),
  /** Every invitation, every tournament: the console's view. */
  invites: () => request<Invite[]>("GET", "/api/super/invites"),
  revokeInvite: (keyId: string) => request<Invite>("DELETE", `/api/super/invites/${keyId}`),

  // ---------- the club library (V2 Phase 5) ----------
  clubs: () => request<ClubSummary[]>("GET", "/api/clubs"),
  /** By id or slug. */
  club: (key: string) => request<Club>("GET", `/api/clubs/${encodeURIComponent(key)}`),
  rosterProfiles: (key: string) =>
    request<RosterProfileLink[]>("GET", `/api/clubs/${encodeURIComponent(key)}/players`),
  clubTournaments: (id: string) =>
    request<ClubTournament[]>("GET", `/api/clubs/${encodeURIComponent(id)}/tournaments`),
  /** A team's record at one event, for the public team profile. */
  teamRecord: (tid: string, teamId: string) =>
    request<TeamRecord>(
      "GET",
      `/api/t/${encodeURIComponent(tid)}/teams/${encodeURIComponent(teamId)}/record`,
    ),
  createClub: (
    input: Omit<Club, "id" | "slug" | "createdAt" | "createdBy" | "updatedAt"> & {
      plays?: boolean;
      number?: string;
      /** The creator's roster row in English; sent only with `plays`. */
      firstName?: string;
      lastName?: string;
    },
  ) => request<Club>("POST", "/api/clubs", input),
  /** `club:write`: the club's own captain or manager, or the super. */
  updateClub: (id: string, patch: ClubPatch) =>
    request<Club>("PUT", `/api/clubs/${encodeURIComponent(id)}`, patch),
  clubMembers: (id: string) =>
    request<ClubMember[]>("GET", `/api/clubs/${encodeURIComponent(id)}/members`),
  setClubMemberRole: (id: string, accountId: string, role: ClubRole) =>
    request<AccountSummary>("PUT", `/api/clubs/${encodeURIComponent(id)}/members/${accountId}`, {
      role,
    }),
  /** Put a seat on the roster: an unclaimed row, or a new row named for the person. */
  linkMemberPlayer: (id: string, accountId: string, input: MemberPlayerLink) =>
    request<AccountSummary>(
      "POST",
      `/api/clubs/${encodeURIComponent(id)}/members/${accountId}/player`,
      input,
    ),
  /** An account re-proves itself with its password; the super sends none. */
  removeClubMember: (id: string, accountId: string, password?: string) =>
    request<{ ok: boolean }>(
      "DELETE",
      `/api/clubs/${encodeURIComponent(id)}/members/${accountId}`,
      {
        ...(password !== undefined && password !== "" ? { password } : { confirm: true }),
      },
    ),
  /** A captain re-proves themselves with their password; the super passes without. */
  resetClubMember: (id: string, accountId: string, password?: string) =>
    request<IssuedInvite>(
      "POST",
      `/api/clubs/${encodeURIComponent(id)}/members/${accountId}/reset`,
      {
        ...(password !== undefined && password !== "" ? { password } : { confirm: true }),
        locale: getLocale(),
      },
    ),
  applyToClub: (clubId: string, input: import("../../shared/club").ApplicationInput) =>
    request<ClubApplication>("POST", `/api/clubs/${encodeURIComponent(clubId)}/apply`, input),
  abandonApplication: () => request<{ ok: boolean }>("DELETE", "/api/account/application", {}),
  applications: (clubId: string) =>
    request<(ClubApplication & { label: string })[]>(
      "GET",
      `/api/clubs/${encodeURIComponent(clubId)}/applications`,
    ),
  decideApplication: (clubId: string, accountId: string, input: ApplicationDecision) =>
    request<{ ok: boolean }>(
      "POST",
      `/api/clubs/${encodeURIComponent(clubId)}/applications/${accountId}`,
      input,
    ),
  clubInvites: (id: string) =>
    request<Invite[]>("GET", `/api/clubs/${encodeURIComponent(id)}/invites`),
  inviteToClub: (id: string, input: { role: ClubRole; label: string; email?: string }) =>
    request<IssuedInvite>("POST", `/api/clubs/${encodeURIComponent(id)}/invites`, {
      ...input,
      locale: getLocale(),
    }),
  revokeClubInvite: (id: string, keyId: string) =>
    request<Invite>("DELETE", `/api/clubs/${encodeURIComponent(id)}/invites/${keyId}`),
  /** `actor` is one person's whole trail; `before` pages older, by the
   *  oldest `at` the caller holds. Cross-tournament: the console's view. */
  audit: (opts: { limit?: number; actor?: string; before?: number } = {}) => {
    const q = new URLSearchParams({ limit: String(opts.limit ?? 200) });
    if (opts.actor !== undefined) q.set("actor", opts.actor);
    if (opts.before !== undefined) q.set("before", String(opts.before));
    return request<AuditEntry[]>("GET", `/api/audit?${q.toString()}`);
  },
  /** Traffic counters, all tournaments, or one when `tournament` is given. */
  analytics: (days = 7, tournament?: string) => {
    const q = new URLSearchParams({ days: String(days) });
    if (tournament !== undefined) q.set("tournament", tournament);
    return request<TrafficReport>("GET", `/api/analytics?${q.toString()}`);
  },
};

/**
 * ONE TOURNAMENT'S API. Every path is `/api/t/<id>/…`, which is what makes a
 * request reach that tournament's Durable Objects and nothing else's
 * (`worker/router.ts`). Components get the instance for the tournament they
 * are rendered under from `useApi()` (`lib/tournament.tsx`); nothing outside
 * a tournament route should ever build one by hand except `/super`, which
 * acts on whichever tournament its operator picked.
 *
 * `paths` are the addresses a PLAIN LINK needs — the calendar feed, the CSV
 * exports, a result card, the regulations file, the chat transport — so no
 * component spells the prefix itself.
 */
export function apiFor(tournamentId: string) {
  const base = `/api/t/${encodeURIComponent(tournamentId)}`;
  const p = (rest: string): string => `${base}${rest}`;
  return {
    tournamentId,
    fieldState: (f: FieldId) => request<StateMessage>("GET", p(`/field/${f}/state`)),
    action: (f: FieldId, action: GameAction) =>
      request<StateMessage>("POST", p(`/field/${f}/action`), action),
    obsCommand: (f: FieldId, cmd: ObsCommand) =>
      request<{ delivered: number }>("POST", p(`/field/${f}/obs`), cmd),
    teams: () => request<Team[]>("GET", p("/teams")),
    createTeam: (t: Omit<Team, "id">) => request<Team>("POST", p("/teams"), t),
    updateTeam: (t: Team) => request<Team>("PUT", p(`/teams/${t.id}`), t),
    deleteTeam: (id: string) => request<{ ok: boolean }>("DELETE", p(`/teams/${id}`)),
    /**
     * A captain editing their own team, from `/team`. Deliberately NOT
     * `updateTeam`: this endpoint carries the roster and the crest and has no
     * field for a name, a code, a colour or a division, so the narrow
     * capability behind it cannot be widened by sending more.
     */
    updateRoster: (teamId: string, body: { roster: Player[]; logo?: string | undefined }) =>
      request<Team>("PUT", p(`/team/${teamId}/roster`), body),
    schedule: () => request<ScheduledGame[]>("GET", p("/schedule")),
    stats: () => request<GameStats[]>("GET", p("/stats")),
    /** The public board's whole read in one request: shared/board.ts has the
     *  Free-plan request arithmetic that makes this one route exist. */
    board: () => request<BoardBundle>("GET", p("/board")),
    fans: () => request<FanState>("GET", p("/fans")),
    voteFan: (choice: FanChoice) => request<FanState>("PUT", p("/fans"), choice),
    /** What is on the fields now, the next kickoff and the last result (2026-09-16). */
    now: () => request<TournamentNow>("GET", p("/now")),
    /** Correct a finished game: snapshot + recorded score, written together.
     *  `allowErase` is the editor's acknowledged intent to wipe a recorded
     *  point list; without it the Worker refuses an empty-over-non-empty save. */
    saveStats: (
      gameId: string,
      stats: Omit<GameStats, "scheduleGameId" | "recordedAt"> & { allowErase?: boolean },
    ) => request<GameStats>("PUT", p(`/stats/${gameId}`), stats),
    createGame: (g: Omit<ScheduledGame, "id">) => request<ScheduledGame>("POST", p("/schedule"), g),
    updateGame: (g: ScheduledGame) => request<ScheduledGame>("PUT", p(`/schedule/${g.id}`), g),
    deleteGame: (id: string) => request<{ ok: boolean }>("DELETE", p(`/schedule/${id}`)),
    fields: () => request<Field[]>("GET", p("/fields")),
    createField: (f: { name: string; order: number }) => request<Field>("POST", p("/fields"), f),
    updateField: (f: Field) => request<Field>("PUT", p(`/fields/${f.id}`), f),
    deleteField: (id: string) => request<{ ok: boolean }>("DELETE", p(`/fields/${id}`)),
    pools: () => request<Pool[]>("GET", p("/pools")),
    createPool: (pool: Omit<Pool, "id">) => request<Pool>("POST", p("/pools"), pool),
    updatePool: (pool: Pool) => request<Pool>("PUT", p(`/pools/${pool.id}`), pool),
    deletePool: (id: string) => request<{ ok: boolean }>("DELETE", p(`/pools/${id}`)),
    divisions: () => request<Division[]>("GET", p("/divisions")),
    createDivision: (d: Omit<Division, "id">) => request<Division>("POST", p("/divisions"), d),
    updateDivision: (d: Division) => request<Division>("PUT", p(`/divisions/${d.id}`), d),
    deleteDivision: (id: string) => request<{ ok: boolean }>("DELETE", p(`/divisions/${id}`)),
    addGames: (games: Omit<ScheduledGame, "id">[]) =>
      request<ScheduledGame[]>("POST", p("/schedule/bulk"), { games }),
    moveGames: (moves: { id: string; x: number; y: number }[]) =>
      request<{ moved: number }>("POST", p("/schedule/move"), { moves }),
    /** The Schedule tab's draft, as one write: `shared/schedule-draft.ts`. */
    applyScheduleBatch: (body: ScheduleBatchBody) =>
      request<{ created: number; updated: number; deleted: number; skipped: number }>(
        "POST",
        p("/schedule/batch"),
        body,
      ),
    /** Cascade retime: the whole slice moves in one call, planned client-side
     *  by `shared/retime.ts` and validated row by row on the way in. */
    retimeGames: (times: Retime[]) =>
      request<{ moved: number }>("POST", p("/schedule/retime"), { times }),
    clearGames: (filter: { divisionId?: string; stage?: "pool" | "bracket"; poolId?: string }) =>
      request<{ removed: number }>("POST", p("/schedule/clear"), filter),

    // ---------- the tournament's own row (V2 Phase 4) ----------
    /** Name, short name, dates, place, time zone; the slug while a draft.
     *  Never the status: `setStatus` is that. */
    resolveMapLink: (link: string) =>
      request<ResolvedMapLink | null>("POST", p("/locations/resolve"), { link }),
    updateMeta: (patch: TournamentPatch) => request<TournamentSummary>("PUT", p("/meta"), patch),
    /** Publish, archive, un-archive. An account proves itself with its
     *  password; a grant has none and confirmed in a dialog instead. */
    setStatus: (status: TournamentStatus, password?: string) =>
      request<TournamentSummary>("POST", p("/status"), {
        status,
        ...(password !== undefined && password !== "" ? { password } : { confirm: true }),
      }),

    // ---------- settings ----------
    settings: () => request<TournamentSettings>("GET", p("/settings")),
    /** A PATCH: send only the sections you are changing. The DO merges them
     *  over the stored row (`SettingsPatch` has the two-admin race this fixed);
     *  the full updated row comes back. */
    saveSettings: (patch: SettingsPatch) =>
      request<TournamentSettings>("PUT", p("/settings"), patch),

    // ---------- the regulations document ----------
    regulationsMeta: () => request<DocMeta | null>("GET", p("/regulations/meta")),
    /** Raw bytes, not JSON: the one upload in the app. The filename rides a
     *  header because the body IS the file. */
    uploadRegulations: async (file: File): Promise<DocMeta> => {
      const res = await fetch(p("/regulations"), {
        method: "PUT",
        credentials: "same-origin",
        headers: {
          [CSRF_HEADER]: "1",
          "content-type": file.type,
          "x-doc-name": encodeURIComponent(file.name),
        },
        body: file,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const known = sentenceFor(body);
        if (known !== null) throw new ApiError(res.status, known.text, known.code);
        throw new ApiError(res.status, `PUT ${p("/regulations")} -> ${res.status}`);
      }
      return (await res.json()) as DocMeta;
    },
    deleteRegulations: () => request<{ ok: boolean }>("DELETE", p("/regulations")),
    regulationsContent: () => request<DocContent | null>("GET", p("/regulations/content")),
    putRegulationsContent: (content: DocContent) =>
      request<{ ok: boolean }>("PUT", p("/regulations/content"), content),

    // ---------- people (cross-team identity) ----------
    /** Confirmed merges + refused pairs. Public: the combined leaderboard needs
     *  them to fold two roster entries into one row. */
    persons: () => request<{ persons: Person[]; dismissed: string[] }>("GET", p("/persons")),
    createPerson: (person: PersonWrite) => request<Person>("POST", p("/persons"), person),
    savePerson: (id: string, person: PersonWrite) =>
      request<Person>("PUT", p(`/persons/${id}`), person),
    /** Un-merge: every member becomes their own person again. */
    deletePerson: (id: string) => request<{ ok: boolean }>("DELETE", p(`/persons/${id}`)),
    dismissPair: (a: string, b: string, dismissed: boolean) =>
      request<{ ok: true }>("POST", p("/persons/dismiss"), { a, b, dismissed }),

    // ---------- push ("your game moved") ----------
    /** The VAPID public key, and the probe for whether this deployment does
     *  push at all: `push-off` when no keypair is configured. */
    pushKey: () => request<{ publicKey: string }>("GET", p("/push/key")),
    /**
     * Register (or refresh) this device. Team-scoped in the PATH, so the
     * credential that authorizes is the participant one rather than a broader
     * organiser link that happens to be in the same browser — the subscription
     * has to land on the grant whose scope names the team, or nothing will
     * ever wake it (`worker/index.ts`).
     */
    pushSubscribe: (teamId: string, body: { endpoint: string; locale: Locale }) =>
      request<{ ok: true }>("POST", p(`/push/team/${teamId}/subscribe`), body),
    pushUnsubscribe: (teamId: string, endpoint: string) =>
      request<{ ok: true }>("DELETE", p(`/push/team/${teamId}/subscribe`), { endpoint }),
    /** Push this credential's own devices with nothing queued: the "did any of
     *  this work" button. `sent` is whether the push SERVICE took it, which is
     *  the hop neither the browser nor the organiser can otherwise see. */
    pushTest: (teamId: string) =>
      request<{ sent: number; devices: number }>("POST", p(`/push/team/${teamId}/test`), {}),

    spiritAwards: () => request<SpiritAward[]>("GET", p("/spirit-awards")),
    saveSpiritAward: (division: string, body: SpiritAwardWrite) =>
      request<SpiritAward>("PUT", p(`/spirit-awards/${encodeURIComponent(division)}`), body),
    // ---------- awards ----------
    /** Shortlists, notes and decisions. `awards:manage`, never public. */
    awards: () => request<Award[]>("GET", p("/awards")),
    createAward: (a: AwardWrite) => request<Award>("POST", p("/awards"), a),
    saveAward: (id: string, a: AwardWrite) => request<Award>("PUT", p(`/awards/${id}`), a),
    deleteAward: (id: string) => request<{ ok: boolean }>("DELETE", p(`/awards/${id}`)),
    /** Announced winners only, and only while the master switch is on. */
    publicAwards: () => request<PublicAward[]>("GET", p("/awards/public")),

    // ---------- spirit ----------
    /** One team's games with the opponent already resolved. Lean on purpose:
     *  this opens on a phone on field wifi and must not carry team logos. */
    spiritTeam: (teamId: string) =>
      request<SpiritTeamView>("GET", p(`/spirit/team/${teamId}/games`)),
    submitSpirit: (teamId: string, gameId: string, body: SpiritWrite) =>
      request<SpiritSubmission>("PUT", p(`/spirit/team/${teamId}/game/${gameId}`), body),
    /** Every submission, comments included: `spirit:read:detail`. */
    spiritAll: () => request<SpiritSubmission[]>("GET", p("/spirit")),
    /** Enter a paper form, correct one, hide a comment, or waive a forfeit. */
    spiritManage: (gameId: string, teamId: string, body: SpiritAdminWrite) =>
      request<SpiritSubmission>("PUT", p(`/spirit/${gameId}/${teamId}`), body),
    removeSpirit: (gameId: string, teamId: string) =>
      request<{ ok: boolean }>("DELETE", p(`/spirit/${gameId}/${teamId}`)),
    /** Whatever the tournament's visibility setting allows. Never comments, and
     *  never raw submissions: standings arrive as server-folded rows. */
    spiritPublic: () => request<PublicSpiritPayload>("GET", p("/spirit/public")),

    // ---------- grants ----------
    grants: () => request<PublicGrant[]>("GET", p("/grants")),
    /** The raw token comes back exactly ONCE: it is not recoverable later, so
     *  the caller must show the link immediately. */
    issueGrant: (g: GrantRequest) =>
      request<{ grant: PublicGrant; token: string }>("POST", p("/grants"), g),
    /** `password` is only needed for an organiser-level grant, which re-prompts. */
    revokeGrant: (keyId: string, password?: string) =>
      request<{ ok: boolean }>("DELETE", p(`/grants/${keyId}`), {
        ...(password !== undefined ? { password } : {}),
      }),

    // ---------- the library: import, promote, sync (V2 Phase 5) ----------
    /** Import a named squad with a stable request id; legacy callers omit it. */
    importTeam: (body: {
      requestId?: string;
      clubId: string;
      roster: ImportChoice[];
      add: Omit<Player, "id">[];
      name?: string;
      short?: string;
      color?: string;
      divisionId?: string;
      poolId?: string;
    }) => request<Team>("POST", p("/teams/import"), body),
    /** An ad-hoc team becomes a club, keeping its id. */
    promoteTeam: (id: string) =>
      request<{ team: Team; club: Club }>("POST", p(`/teams/${id}/promote`), {}),
    /** This tournament's roster into the library, additively. */
    syncTeam: (teamId: string) => request<Club>("POST", p(`/team/${teamId}/sync`), {}),
    /** A club entering this event itself, while its registration is open
     *  New submissions keep their request id across retries and choose a squad name. */
    register: (
      clubId: string,
      body: {
        roster: ImportChoice[];
        add: Omit<Player, "id">[];
        divisionId?: string;
        requestId?: string;
        name?: string;
        short?: string;
        needsPickups?: boolean;
        leadership?: TeamRoleSelection;
      },
    ) =>
      request<Team & { entryStatus: EventEntry["status"] }>(
        "POST",
        p(`/register/${encodeURIComponent(clubId)}`),
        body,
      ),
    /** ...and withdrawing while it still is. */
    withdraw: (clubId: string, teamId = clubId) =>
      request<{ ok: boolean }>(
        "DELETE",
        p(`/register/${encodeURIComponent(clubId)}?team=${encodeURIComponent(teamId)}`),
      ),
    entries: () => request<EventEntry[]>("GET", p("/entries")),
    clubEntries: (id: string) =>
      request<EventEntry[]>("GET", p(`/register/${encodeURIComponent(id)}/entries`)),
    decideEntry: (id: string, body: EntryDecision) =>
      request<EventEntry>("POST", p(`/entries/${encodeURIComponent(id)}/decision`), body),
    pickupBoard: () => request<PickupBoard>("GET", p("/pickup-board")),
    setPickupAvailability: (body: PickupAvailabilityInput) =>
      request<{ ok: boolean }>("PUT", p("/pickup-availability"), body),
    /** Public: the roster rows at this event with a public profile, every club at once. */
    profiles: () => request<RosterProfileLink[]>("GET", p("/profiles")),
    /** Public: the squad's event captain and spirit captain as roster row ids. */
    teamRoles: (teamId: string) =>
      request<PublicTeamRoles>("GET", p(`/teams/${encodeURIComponent(teamId)}/roles`)),
    teamLeadership: (teamId: string) =>
      request<TeamLeadership>("GET", p(`/team/${encodeURIComponent(teamId)}/leadership`)),
    setTeamLeadership: (teamId: string, body: TeamRoleSelection) =>
      request<TeamLeadership>("PUT", p(`/team/${encodeURIComponent(teamId)}/leadership`), body),
    pickupListings: () => request<string[]>("GET", p("/pickups")),
    setPickupListing: (id: string, needsPickups: boolean) =>
      request<{ needsPickups: boolean }>("PUT", p(`/team/${id}/pickups`), { needsPickups }),
    playerRequests: (id: string) =>
      request<PlayerRequest[]>("GET", p(`/team/${id}/player-requests`)),
    requestPlayer: (id: string, body: PlayerRequestInput) =>
      request<PlayerRequest>("POST", p(`/team/${id}/player-requests`), body),
    withdrawPlayer: (teamId: string, id: string) =>
      request<{ ok: boolean }>("DELETE", p(`/team/${teamId}/player-requests/${id}`)),
    decidePlayer: (teamId: string, id: string, body: EntryDecision) =>
      request<PlayerRequest>("POST", p(`/team/${teamId}/player-requests/${id}/decision`), body),
    /** Any registered player, listed or not (`pickup:invite`, 2026-09-15). */
    invitePickup: (teamId: string, body: PlayerRequestInput) =>
      request<PlayerRequest>("POST", p(`/team/${teamId}/pickup-invites`), body),
    pickupLinks: (teamId: string) =>
      request<PickupLink[]>("GET", p(`/team/${teamId}/pickup-links`)),
    issuePickupLink: (teamId: string) =>
      request<IssuedPickupLink>("POST", p(`/team/${teamId}/pickup-links`), {}),
    revokePickupLink: (teamId: string, keyId: string) =>
      request<{ ok: boolean }>(
        "DELETE",
        p(`/team/${teamId}/pickup-links/${encodeURIComponent(keyId)}`),
      ),

    // ---------- invitations for this tournament's teams (V2 Phase 3) ----------
    invites: () => request<Invite[]>("GET", p("/invites")),
    /** A captain, spirit captain or manager for ONE team: the membership
     *  granted is the club's, and the team is the club (D10). */
    inviteClub: (teamId: string, input: { role: ClubRole; label: string; email?: string }) =>
      request<IssuedInvite>("POST", p(`/team/${teamId}/invites`), {
        ...input,
        locale: getLocale(),
      }),
    revokeInvite: (keyId: string) => request<Invite>("DELETE", p(`/invites/${keyId}`)),

    // ---------- backup ----------
    /** This tournament's whole dump (ROADMAP §0). Superadmin-only today. */
    exportAll: () => request<ExportBundle>("GET", p("/export")),
    /** This tournament's own trail (V2 Phase 6, `audit:read:own`): the same
     *  reader as the console's, filtered to this tournament in the AuthDO. */
    audit: (opts: { limit?: number; actor?: string; before?: number } = {}) => {
      const q = new URLSearchParams({ limit: String(opts.limit ?? 200) });
      if (opts.actor !== undefined) q.set("actor", opts.actor);
      if (opts.before !== undefined) q.set("before", String(opts.before));
      return request<AuditEntry[]>("GET", p(`/audit?${q.toString()}`));
    },

    // ---------- addresses for plain links ----------
    paths: {
      /** The calendar feed: everything, or one team's games. */
      ics: (teamId?: string): string =>
        teamId === undefined
          ? p("/schedule.ics")
          : p(`/schedule.ics?team=${encodeURIComponent(teamId)}`),
      /** A finished game's result card. */
      card: (gameId: string): string => p(`/game/${encodeURIComponent(gameId)}/card.png`),
      statsCsv: p("/stats/export.csv"),
      spiritCsv: p("/spirit/export.csv"),
      /** The uploaded Регламент, as a file. */
      regulationsFile: p("/regulations"),
      /** The schedule assistant's streaming transport (`AI-AGENT.md`). */
      chat: p("/chat"),
    },
  };
}

export type TournamentApi = ReturnType<typeof apiFor>;

/**
 * Socket URL for one field of one tournament. The role is an INTENT only: the
 * server grants it from the session cookie riding the upgrade request, and
 * never from anything in the query string. Cloudflare logs request URLs,
 * which is why the old `?key=…` had to go.
 *
 * `src` is which PUBLIC surface is connecting (overlay vs board), for the
 * traffic counters only: the server validates it to a closed set and derives
 * the credentialed surfaces from tags, never from this.
 */
/** A support chat's socket (2026-09-16): the cookie or the session rides
 *  the upgrade, the query string carries nothing. */
export function supportWsUrl(chatId: string): string {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}/api/support/chats/${encodeURIComponent(chatId)}/ws`;
}

export function wsUrl(
  tournamentId: string,
  f: FieldId,
  role?: "admin" | "bridge",
  src?: "overlay" | "board",
): string {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  const params = new URLSearchParams();
  if (role !== undefined) params.set("role", role);
  if (src !== undefined) params.set("src", src);
  const qs = params.toString();
  return `${proto}://${window.location.host}/api/t/${encodeURIComponent(tournamentId)}/field/${f}/ws${qs.length > 0 ? `?${qs}` : ""}`;
}

export function bridgeWsUrl(tournamentId: string, f: FieldId): string {
  return wsUrl(tournamentId, f, "bridge");
}

/** The DO closes a socket with this code when its grant is revoked. */
export const WS_REVOKED = 4003;
